import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { useLanguage } from "../../i18n";

interface FoundingStatus {
  active: boolean;
  limit: number;
  claimed: number;
  remaining: number;
  months: number;
  premiumLimit?: number;
  premiumRemaining?: number;
  nextPlan?: "pro" | "premium";
}

function useFoundingStatus(): FoundingStatus | null {
  const [s, setS] = useState<FoundingStatus | null>(null);
  useEffect(() => {
    let live = true;
    api.get("/api/offers/founding").then((d) => live && setS(d)).catch(() => { });
    return () => { live = false; };
  }, []);
  return s;
}

// "Founding Owner offer: Pro free for 3 months. 312 of 500 spots left."
// Only shown while the offer is running and spots remain. Used on the
// landing page and under the owner choice in the sign-up wizard.
export function FoundingOfferStrip({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const s = useFoundingStatus();
  if (!s || !s.active) return null;
  const premium = s.nextPlan === "premium" && (s.premiumRemaining ?? 0) > 0;
  const pct = premium
    ? Math.min(100, Math.round((s.claimed / Math.max(1, s.premiumLimit ?? 1)) * 100))
    : Math.min(100, Math.round((s.claimed / Math.max(1, s.limit)) * 100));
  return (
    <div
      style={{
        margin: compact ? "12px 0" : "18px 0",
        padding: "14px 16px",
        borderRadius: 16,
        border: "1.5px solid #f2965c",
        background: "rgba(242,150,92,.12)",
        textAlign: "left",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: compact ? 14 : 16 }}>
        {premium ? t("foundingOffer.titlePremium", { months: s.months }) : t("foundingOffer.title", { months: s.months })}
      </div>
      <div style={{ fontSize: 13, marginTop: 2, opacity: 0.85 }}>
        {premium
          ? t("foundingOffer.bodyPremium", { remaining: s.premiumRemaining ?? 0, premiumLimit: s.premiumLimit ?? 0, limit: s.limit })
          : t("foundingOffer.body", { remaining: s.remaining, limit: s.limit })}
      </div>
      <div style={{ height: 6, borderRadius: 99, background: "rgba(0,0,0,.12)", marginTop: 10, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "#f2965c", borderRadius: 99 }} />
      </div>
    </div>
  );
}

// Owner dashboard note for a Founding Owner: their number and when the free
// Pro ends. Dismissible for the session.
export function FoundingOwnerNotice() {
  const { t } = useLanguage();
  const [mine, setMine] = useState<{ isFounding: boolean; spot: number | null; endsAt: string | null; daysLeft: number; price: number; planName?: string } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/api/offers/founding/mine").then((d) => live && setMine(d)).catch(() => { });
    return () => { live = false; };
  }, []);
  if (!mine || !mine.isFounding || mine.daysLeft <= 0 || dismissed || !mine.endsAt) return null;
  const date = new Date(mine.endsAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="card" style={{ marginBottom: 14, borderColor: "#f2965c", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 13 }}>{t("foundingOffer.noticeTitle", { spot: mine.spot ?? 0 })}</b>
        <div className="sub" style={{ fontSize: 12 }}>{t("foundingOffer.noticeBody", { date, days: mine.daysLeft, price: mine.price, plan: mine.planName ?? "Pro" })}</div>
      </div>
      <button type="button" className="link-btn" aria-label="Dismiss" onClick={() => setDismissed(true)}>✕</button>
    </div>
  );
}
